import { useState, useEffect, useCallback, useMemo } from 'react';
import { ENDPOINTS } from '../endpoints';
import { authHeaders } from '../api';

// Convierte un Date al string YYYY-MM-DD usando la fecha LOCAL (no UTC).
export const toLocalDateString = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const DOW_ABBR = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];

// ── Claves de slot y de matriz ───────────────────────────────────────────────
// Una sección puede tener varias clases el mismo día en distintos BLOQUES del
// horario. El "slot" es (fecha, bloque). BlockNumber es 1-based.
export const slotKey = (date, block) => `${date}#${block || 1}`;
export const parseSlotKey = (k) => {
  const h = k.indexOf('#');
  if (h === -1) return { date: k, block: 1 };
  return { date: k.slice(0, h), block: parseInt(k.slice(h + 1), 10) || 1 };
};
// Clave de asignación OA en la matriz: `${fecha}#${bloque}|${oaId}`
export const matrixKey = (date, block, oaId) => `${slotKey(date, block)}|${oaId}`;
export const parseMatrixKey = (k) => {
  const bar = k.lastIndexOf('|');
  const s = parseSlotKey(k.slice(0, bar));
  return { date: s.date, block: s.block, oaId: k.slice(bar + 1) };
};

const fmtTime = (t) => (typeof t === 'string' ? t.slice(0, 5) : '');
const blockLabelFor = (b, schedBlocks) => {
  const blk = schedBlocks[b - 1];
  if (blk && blk.start) return `${fmtTime(blk.start)}–${fmtTime(blk.end)}`;
  return `Bloque ${b}`;
};

// Genera las columnas (fecha, bloque) dentro de [rangeStart, rangeEnd].
// `blocksByDow`: { dow(1-7) -> [{start,end}, ...] }. `maxBlockByDate`: { fecha ->
// mayor bloque con clase existente } — así una clase huérfana (bloque que ya no
// está en el horario) sigue teniendo columna.
export function buildSlotColumns(rangeStart, rangeEnd, blocksByDow, blockedDates, maxBlockByDate) {
  if (!rangeStart || !rangeEnd || rangeStart > rangeEnd) return [];
  const cols = [];
  const end = new Date(rangeEnd + 'T00:00:00');
  const cur = new Date(rangeStart + 'T00:00:00');
  while (cur <= end) {
    const dow = cur.getDay() === 0 ? 7 : cur.getDay();
    const dateString = toLocalDateString(cur);
    const schedBlocks = blocksByDow[dow] || [];
    const n = Math.max(schedBlocks.length, (maxBlockByDate && maxBlockByDate[dateString]) || 0);
    if (n > 0 && !blockedDates.has(dateString)) {
      for (let b = 1; b <= n; b++) {
        cols.push({
          dateString,
          blockNumber: b,
          blockLabel: blockLabelFor(b, schedBlocks),
          blockCount: n,
          dayOfMonth: cur.getDate(),
          dowAbbr: DOW_ABBR[cur.getDay()],
          month: cur.getMonth(),
          year: cur.getFullYear(),
        });
      }
    }
    cur.setDate(cur.getDate() + 1);
  }
  return cols;
}

// Agrupa columnas consecutivas por mes, para el encabezado superior.
export function buildMonthGroups(columns) {
  const groups = [];
  columns.forEach(col => {
    const last = groups[groups.length - 1];
    if (last && last.month === col.month && last.year === col.year) {
      last.count += 1;
    } else {
      const label = new Date(col.year, col.month, 1).toLocaleString('es-ES', { month: 'long' });
      groups.push({ key: `${col.year}-${col.month}`, label, month: col.month, year: col.year, count: 1 });
    }
  });
  return groups;
}

// Datos y lógica de la matriz OA x Clase (docs/oa_modif.md). La instancia se
// crea en TeacherView y se comparte entre las mini-matrices por unidad del
// acordeón de "Mis Clases" (UnitOAClassMatrix.js) y el botón "Actualizar" del
// calendario — un solo fetch por sección en vez de uno por unidad.
export default function useOAClassMatrix(selectedCourseSection) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [blocksByDow, setBlocksByDow] = useState({}); // { dow -> [{start,end}] }
  const [blockedDates, setBlockedDates] = useState(new Set());
  const [matrixClasses, setMatrixClasses] = useState([]); // [{planned_class_id, class_date, block_number, approval_status}]
  const [assignments, setAssignments] = useState(new Set()); // Set de "fecha#bloque|oaId"
  const [pendingChanges, setPendingChanges] = useState({}); // "fecha#bloque|oaId" -> boolean
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Descarta ediciones pendientes al cambiar de sección (pertenecen a otra sección).
  useEffect(() => {
    setPendingChanges({});
  }, [selectedCourseSection]);

  // Horario académico (por bloques) y eventos de calendario de la sección.
  // Cache-busting (`?t=`) porque el docente debe ver de inmediato los cambios
  // que el admin haga en "Configuración de Horario".
  const loadScheduleAndEvents = useCallback(() => {
    if (!selectedCourseSection) return;
    const ts = Date.now();
    const bust = (url) => url + (url.includes('?') ? '&' : '?') + `t=${ts}`;
    fetch(bust(ENDPOINTS.ADMIN_ORGANIZATION_SCHEDULE(selectedCourseSection)), { headers: authHeaders(), cache: 'no-store' })
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        const map = {};
        (Array.isArray(data) ? data : []).forEach(s => {
          const dow = s.day_of_week;
          if (!dow) return;
          (map[dow] = map[dow] || []).push({ start: s.start_time, end: s.end_time });
        });
        Object.values(map).forEach(list => list.sort((a, b) => String(a.start).localeCompare(String(b.start))));
        setBlocksByDow(map);
      })
      .catch(() => setBlocksByDow({}));

    fetch(bust(ENDPOINTS.ADMIN_SECTION_CALENDAR_EVENTS(selectedCourseSection)), { headers: authHeaders(), cache: 'no-store' })
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        setBlockedDates(new Set(Array.isArray(data) ? data.map(ev => ev.event_date) : []));
      })
      .catch(() => setBlockedDates(new Set()));
  }, [selectedCourseSection]);

  useEffect(() => { loadScheduleAndEvents(); }, [loadScheduleAndEvents]);

  const scheduleDays = useMemo(
    () => new Set(Object.keys(blocksByDow).map(Number)),
    [blocksByDow]
  );

  const loadMatrix = useCallback(async () => {
    if (!selectedCourseSection) return;
    // El horario puede haber cambiado en la Vista Admin — refrescarlo también.
    loadScheduleAndEvents();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${ENDPOINTS.OA_CLASS_MATRIX(selectedCourseSection)}?t=${Date.now()}`,
        { headers: authHeaders(), cache: 'no-store' }
      );
      if (!res.ok) throw new Error('No se pudo cargar la matriz de planificación');
      const data = await res.json();
      const classes = data.classes || [];
      setMatrixClasses(classes);
      setStartDate(data.start_date || '');
      setEndDate(data.end_date || '');

      const classById = new Map(classes.map(c => [c.planned_class_id, c]));
      const assignedKeys = (data.assignments || [])
        .map(a => {
          const cls = classById.get(a.planned_class_id);
          return cls ? matrixKey(cls.class_date, cls.block_number, a.competency_definition_id) : null;
        })
        .filter(Boolean);
      setAssignments(new Set(assignedKeys));
    } catch (err) {
      setError(err.message || 'Error al cargar la matriz');
    } finally {
      setLoading(false);
    }
  }, [selectedCourseSection, loadScheduleAndEvents]);

  useEffect(() => { loadMatrix(); }, [loadMatrix]);

  // fecha -> mayor bloque con clase existente (para conservar clases huérfanas).
  const maxBlockByDate = useMemo(() => {
    const map = {};
    matrixClasses.forEach(c => {
      const b = c.block_number || 1;
      if (!map[c.class_date] || b > map[c.class_date]) map[c.class_date] = b;
    });
    return map;
  }, [matrixClasses]);

  const dateColumns = useMemo(
    () => buildSlotColumns(startDate, endDate, blocksByDow, blockedDates, maxBlockByDate),
    [startDate, endDate, blocksByDow, blockedDates, maxBlockByDate]
  );

  const monthGroups = useMemo(() => buildMonthGroups(dateColumns), [dateColumns]);

  // Columnas (fecha, bloque) para un sub-rango arbitrario (matriz por unidad),
  // siempre recortado al semestre en curso.
  const computeDateColumns = useCallback((rangeStart, rangeEnd) => {
    if (!startDate || !endDate || !rangeStart) return [];
    const lo = rangeStart < startDate ? startDate : rangeStart;
    const hi = !rangeEnd || rangeEnd > endDate ? endDate : rangeEnd;
    return buildSlotColumns(lo, hi, blocksByDow, blockedDates, maxBlockByDate);
  }, [startDate, endDate, blocksByDow, blockedDates, maxBlockByDate]);

  // Siguiente día con horario académico (sin días bloqueados) estrictamente
  // posterior a `dateString`, dentro del semestre. '' si no hay ninguno — se usa
  // para prellenar la fecha de inicio de la unidad siguiente sin solaparla.
  const getNextClassDay = useCallback((dateString) => {
    if (!dateString || scheduleDays.size === 0) return '';
    const d = new Date(dateString + 'T00:00:00');
    for (let i = 0; i < 366; i++) {
      d.setDate(d.getDate() + 1);
      const ds = toLocalDateString(d);
      if (endDate && ds > endDate) return '';
      const dayNum = d.getDay() === 0 ? 7 : d.getDay();
      if (scheduleDays.has(dayNum) && !blockedDates.has(ds)) return ds;
    }
    return '';
  }, [scheduleDays, blockedDates, endDate]);

  // Estado de aprobación por slot "fecha#bloque".
  const approvalByDateBlock = useMemo(() => {
    const map = {};
    matrixClasses.forEach(c => { map[slotKey(c.class_date, c.block_number)] = c.approval_status; });
    return map;
  }, [matrixClasses]);

  // Clase por slot "fecha#bloque" con la marca `hasContent`: "planificada" =
  // tiene un objetivo escrito o un Type distinto del de por defecto.
  const plannedClassBySlot = useMemo(() => {
    const map = {};
    matrixClasses.forEach(c => {
      const hasContent = !!(
        (c.objective && c.objective.trim()) ||
        (c.type && c.type !== 'Aplicación del OA')
      );
      map[slotKey(c.class_date, c.block_number)] = {
        class_date: c.class_date,
        block_number: c.block_number,
        objective: c.objective || '',
        type: c.type || '',
        approval_status: c.approval_status,
        hasContent,
      };
    });
    return map;
  }, [matrixClasses]);

  // Dado un conjunto de claves de matriz ("fecha#bloque|oaId"), devuelve las
  // clases planificadas (con contenido) y no aprobadas cuyos OA se están
  // MODIFICANDO — para avisar al docente de que continuar borrará la planificación
  // de esa clase. Una entrada por slot.
  //  - `includeAdds: false` (por defecto): solo cuenta desmarcados de OA ya
  //    asociados (flujo "Desasociar fuera de rango").
  //  - `includeAdds: true`: cuenta además marcar un OA nuevo en una clase ya
  //    planificada (flujo botón "Actualizar").
  const getPlannedClassChangeTargets = useCallback((keys, { includeAdds = false } = {}) => {
    const bySlot = new Map();
    (keys || []).forEach(key => {
      const persisted = assignments.has(key);
      const pending = key in pendingChanges ? pendingChanges[key] : null;
      const isRemove = persisted && pending !== true;   // asociado en BD y no re-marcado
      const isAdd = !persisted && pending === true;     // no asociado y recién marcado
      if (!(isRemove || (includeAdds && isAdd))) return;
      const { date, block } = parseMatrixKey(key);
      const slot = slotKey(date, block);
      const c = plannedClassBySlot[slot];
      if (!c || !c.hasContent || c.approval_status === 'Aprobada') return;
      if (!bySlot.has(slot)) {
        bySlot.set(slot, {
          slot,
          class_date: date,
          block_number: block,
          objective: c.objective,
          type: c.type,
          keys: [],  // claves "fecha#bloque|oaId" que se modifican en este slot
        });
      }
      bySlot.get(slot).keys.push(key);
    });
    return [...bySlot.values()];
  }, [plannedClassBySlot, assignments, pendingChanges]);

  // Descarta cambios pendientes de las claves indicadas: vuelven a su estado
  // persistido (checkbox restaurado). Para el botón "Restaurar" del modal de
  // aviso "Esta clase ya tiene una planificación".
  const revertPendingKeys = useCallback((keys) => {
    if (!keys || keys.length === 0) return;
    setPendingChanges(prev => {
      const next = { ...prev };
      keys.forEach(key => { delete next[key]; });
      return next;
    });
  }, []);

  const isChecked = useCallback((date, block, oaId) => {
    const key = matrixKey(date, block, oaId);
    return key in pendingChanges ? pendingChanges[key] : assignments.has(key);
  }, [pendingChanges, assignments]);

  const toggleCell = useCallback((date, block, oaId) => {
    if (approvalByDateBlock[slotKey(date, block)] === 'Aprobada') return;
    const key = matrixKey(date, block, oaId);
    const current = key in pendingChanges ? pendingChanges[key] : assignments.has(key);
    setPendingChanges(prev => ({ ...prev, [key]: !current }));
  }, [approvalByDateBlock, pendingChanges, assignments]);

  const pendingCount = Object.keys(pendingChanges).length;

  // Refleja en `assignments` los cambios ya persistidos por el backend
  // (result.applied de save_oa_class_matrix), sin volver a pedir la matriz.
  const applyAppliedToAssignments = useCallback((applied) => {
    if (!Array.isArray(applied) || applied.length === 0) return;
    setAssignments(prev => {
      const next = new Set(prev);
      applied.forEach(c => {
        const key = matrixKey(c.class_date, c.block_number, c.competency_definition_id);
        if (c.checked) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  }, []);

  const _changeFromKey = (key, checked) => {
    const { date, block, oaId } = parseMatrixKey(key);
    return { class_date: date, block_number: block, competency_definition_id: parseInt(oaId, 10), checked };
  };

  // Guarda los cambios pendientes indicados por `keys` (o todos, si se omite).
  // `options.clearPlannedSlots` (Set de "fecha#bloque"): slots cuya planificación
  // debe descartarse al aplicar el cambio — sea al desmarcar un OA asociado o al
  // marcar uno nuevo en una clase ya planificada (el docente lo confirmó en el
  // modal de aviso).
  const saveChanges = useCallback(async (keys, options = {}) => {
    const clearPlannedSlots = options.clearPlannedSlots || null;
    const targetKeys = keys || Object.keys(pendingChanges);
    const changes = targetKeys
      .filter(key => key in pendingChanges)
      .map(key => {
        const change = _changeFromKey(key, pendingChanges[key]);
        if (
          clearPlannedSlots &&
          clearPlannedSlots.has(slotKey(change.class_date, change.block_number))
        ) {
          change.clear_planning = true;
        }
        return change;
      });
    if (changes.length === 0) return { ok: true, rejected: [] };
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(ENDPOINTS.OA_CLASS_MATRIX_SAVE(selectedCourseSection), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ changes }),
      });
      if (!res.ok) throw new Error('No se pudo guardar la matriz de planificación');
      const result = await res.json();
      if (result.rejected && result.rejected.length > 0) {
        setError(`${result.rejected.length} cambio(s) no se aplicaron porque la clase ya fue aprobada.`);
      } else {
        setSuccess('Cambios guardados exitosamente');
        setTimeout(() => setSuccess(''), 3000);
      }
      applyAppliedToAssignments(result.applied);
      setPendingChanges(prev => {
        const next = { ...prev };
        targetKeys.forEach(key => { delete next[key]; });
        return next;
      });
      // Si se descartó la planificación de alguna clase, releer la matriz para
      // reflejar el objetivo/tipo ya borrados (no basta con applyApplied...).
      if (changes.some(c => c.clear_planning)) loadMatrix();
      return { ok: true, rejected: result.rejected || [] };
    } catch (err) {
      setError(err.message || 'Error al guardar los cambios');
      return { ok: false, rejected: [] };
    } finally {
      setSaving(false);
    }
  }, [pendingChanges, selectedCourseSection, applyAppliedToAssignments, loadMatrix]);

  // Desasocia (checked:false) de inmediato los pares indicados, persistiendo
  // directo en el endpoint. Omite slots de clases aprobadas (no editables).
  // `options.clearPlannedSlots` (Set de "fecha#bloque"): slots cuya planificación
  // debe descartarse al desasociar (confirmado por el docente en el modal).
  const unassignKeys = useCallback(async (keys, options = {}) => {
    const clearPlannedSlots = options.clearPlannedSlots || null;
    const changes = [];
    (keys || []).forEach(key => {
      const { date, block, oaId } = parseMatrixKey(key);
      if (approvalByDateBlock[slotKey(date, block)] === 'Aprobada') return;
      const change = { class_date: date, block_number: block, competency_definition_id: parseInt(oaId, 10), checked: false };
      if (clearPlannedSlots && clearPlannedSlots.has(slotKey(date, block))) {
        change.clear_planning = true;
      }
      changes.push(change);
    });
    if (changes.length === 0) return { ok: true, rejected: [] };
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(ENDPOINTS.OA_CLASS_MATRIX_SAVE(selectedCourseSection), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ changes }),
      });
      if (!res.ok) throw new Error('No se pudo guardar la matriz de planificación');
      const result = await res.json();
      if (result.rejected && result.rejected.length > 0) {
        setError(`${result.rejected.length} cambio(s) no se aplicaron porque la clase ya fue aprobada.`);
      }
      applyAppliedToAssignments(result.applied);
      setPendingChanges(prev => {
        const next = { ...prev };
        (keys || []).forEach(key => { delete next[key]; });
        return next;
      });
      if (changes.some(c => c.clear_planning)) loadMatrix();
      return { ok: true, rejected: result.rejected || [] };
    } catch (err) {
      setError(err.message || 'Error al guardar los cambios');
      return { ok: false, rejected: [] };
    } finally {
      setSaving(false);
    }
  }, [approvalByDateBlock, selectedCourseSection, applyAppliedToAssignments, loadMatrix]);

  return {
    startDate,
    endDate,
    semesterStart: startDate,
    semesterEnd: endDate,
    loading,
    saving,
    error,
    setError,
    success,
    setSuccess,
    dateColumns,
    monthGroups,
    computeDateColumns,
    getNextClassDay,
    approvalByDateBlock,
    plannedClassBySlot,
    blockedDates,
    assignments,
    pendingChanges,
    pendingCount,
    isChecked,
    toggleCell,
    saveChanges,
    unassignKeys,
    loadMatrix,
    matrixClasses,
    getPlannedClassChangeTargets,
    revertPendingKeys,
  };
}
